/**
 * Console API client. Talks to the destroy-console-api worker.
 *
 * Auth: magic-code email login. After /auth/verify we receive a signed
 * JWT and store it in localStorage. Every subsequent request sends it
 * as a Bearer token. A 401 response invalidates the stored token so
 * the UI bounces back to the login page.
 */

const API_BASE =
  (typeof window !== "undefined" &&
    (window as unknown as { __DESTROY_CONSOLE_API__?: string })
      .__DESTROY_CONSOLE_API__) ||
  import.meta.env.VITE_CONSOLE_API ||
  "/console-api";

const TOKEN_STORAGE_KEY = "destroy_console_token";

export function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setStoredToken(token: string | null): void {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function request<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getStoredToken();
  const res = await fetch(`${API_BASE}${endpoint}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  });

  if (res.status === 401) {
    const error = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    // Only drop the cached JWT for explicit token-bad signals
    // (signature failure / expired / explicitly invalid). Anything
    // else (worker booting, transient 401 from a downstream proxy,
    // operator allowlist temporarily empty after a local migration
    // reset) keeps the token so a quick refresh recovers without
    // forcing a fresh email-code login. The console worker emits
    // these specific error codes for genuinely-revoked sessions.
    const errCode = typeof error.error === "string" ? error.error : "";
    const TOKEN_BAD = new Set(["INVALID_SESSION", "NO_SESSION", "NOT_AN_OPERATOR"]);
    if (TOKEN_BAD.has(errCode)) {
      setStoredToken(null);
    }
    throw new ApiError(
      typeof error.message === "string" ? error.message : "Session expired",
      401,
      error,
    );
  }

  if (!res.ok) {
    const error = (await res.json().catch(() => ({
      message: "Request failed",
    }))) as Record<string, unknown>;
    const message =
      (typeof error.message === "string" ? error.message : null) ||
      `HTTP ${res.status}`;
    throw new ApiError(message, res.status, error);
  }

  return res.json();
}

export interface ConsoleSession {
  consoleUserId: string;
  email: string;
  role: "admin" | "viewer";
  loadedAt: string;
}

export type ConsoleTier = "free" | "pro" | "business";

export interface ConsoleUserRow {
  id: string;
  email: string;
  role: "admin" | "viewer";
  subscriptionTier: ConsoleTier | "enterprise";
  subscriptionStatus: string;
  subscriptionSource?: string | null;
  pastDueSince?: string | null;
  apiCredits?: number;
  stripeCustomerId: string | null;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string | null;
  lastSeenAt?: string | null;
}

export interface ConsoleSubscriptionRow {
  id: string;
  status: string;
  tier: string;
  price_id: string | null;
  interval: string | null;
  amount_cents: number | null;
  currency: string | null;
  current_period_end: string | null;
  cancel_at_period_end: number;
  canceled_at: string | null;
  updated_at: string;
}

export interface ConsoleInvoiceRow {
  id: string;
  status: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string | null;
  attempt_count: number;
  hosted_invoice_url: string | null;
  period_end: string | null;
  paid_at: string | null;
  failed_at: string | null;
  created_at: string;
}

export interface ConsoleCompRow {
  id: string;
  kind: string;
  amount: number;
  reason: string | null;
  granted_by: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface ConsoleUserDetail extends ConsoleUserRow {
  subscriptions: ConsoleSubscriptionRow[];
  invoices: ConsoleInvoiceRow[];
  comps: ConsoleCompRow[];
  recentInboxes: { id: string; address: string; expires_at: string; created_at: string }[];
  apiKeys: { id: string; name: string; key_prefix: string; created_at: string }[];
}

export interface ConsoleBillingOverview {
  mrr: number;
  mrrCents: number;
  byTier: Record<string, { subs: number; mrrCents: number }>;
  pastDueAccounts: number;
  lastWebhookAt: string | null;
  lastWebhookType: string | null;
  lastInvoiceAt: string | null;
}

export interface ConsoleChargeRow {
  id: string;
  amount: number;
  amountRefunded: number;
  currency: string;
  status: string;
  description: string | null;
  receiptUrl: string | null;
  created: number;
  refunded: boolean;
}

export interface ConsoleBackfillReport {
  success: boolean;
  customersChecked: number;
  subscriptions: number;
  invoices: number;
  /** Rows skipped because they belong to another product on the same Stripe account. */
  skippedForeign: number;
  tiersApplied: number;
  errors: string[];
}

export interface ConsoleSubscriptionHistoryRow {
  id: string;
  user_id: string | null;
  email: string | null;
  stripe_customer_id: string;
  status: string;
  tier: string;
  interval: string | null;
  amount_cents: number | null;
  currency: string | null;
  current_period_end: string | null;
  cancel_at_period_end: number;
  canceled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConsoleInvoiceHistoryRow {
  id: string;
  user_id: string | null;
  email: string | null;
  stripe_customer_id: string;
  subscription_id: string | null;
  status: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string | null;
  attempt_count: number;
  hosted_invoice_url: string | null;
  period_end: string | null;
  paid_at: string | null;
  failed_at: string | null;
  created_at: string;
}

export interface ConsolePruneReport {
  dryRun: boolean;
  subscriptions: number;
  invoices: number;
}

export interface ConsoleReconcileReport {
  checked: number;
  changed: number;
  errors: number;
  ranAt: string;
  changes: {
    userId: string;
    email: string;
    from: string;
    to: string;
    status: string;
    reason: string;
  }[];
}

export interface ConsoleInboxRow {
  id: string;
  userId: string | null;
  address: string;
  domain: string;
  expiresAt: string;
  createdAt: string;
  extensionCount: number;
  createdVia: string | null;
}

export interface ConsoleOperatorRow {
  id: string;
  email: string;
  role: "admin" | "viewer";
  created_at: string;
  updated_at: string;
  revoked_at: string | null;
  expires_at: string | null;
  invited_by: string | null;
  notes: string | null;
}

export interface ConsoleAuditEntry {
  id: string;
  console_user_id: string;
  email: string;
  action: string;
  target_kind: string | null;
  target_id: string | null;
  meta: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

// ============================================================
// Inbox analytics from /admin/inboxes - mirrored from the legacy
// public-app admin client. Rich snapshot used on the console
// inboxes page (overview, distributions, activity, top + recent).
// ============================================================

export interface AdminInboxAnalytics {
  overview: {
    total: number;
    current: number;
    active: number;
    expired: number;
    withForwarding: number;
    totalMessages: number;
    currentMessages: number;
  };
  byOwnerType: {
    anonymous: number;
    authenticated: number;
  };
  byTier: Record<string, number>;
  byDomain: Array<{ domain: string; count: number }>;
  bySource: Record<string, number>;
  activity: {
    inboxesCreated: Array<{ date: string; count: number }>;
    messagesReceived: Array<{ date: string; count: number }>;
  };
  topInboxes: Array<{
    id: string;
    address: string;
    domain: string;
    ownerEmail: string | null;
    ownerTier: string | null;
    messageCount: number;
    expiresAt: string;
    createdAt: string;
  }>;
  recentInboxes: Array<{
    id: string;
    address: string;
    domain: string;
    ownerEmail: string | null;
    ownerTier: string | null;
    expiresAt: string;
    createdAt: string;
    messageCount: number;
  }>;
  activeInboxes: Array<{
    id: string;
    address: string;
    domain: string;
    ownerEmail: string | null;
    ownerTier: string | null;
    expiresAt: string;
    createdAt: string;
    messageCount: number;
  }>;
}

// ============================================================
// Domain Health types - mirrored from the legacy public-app admin
// client. Backed by /admin/domains/health* on the console worker.
// ============================================================

export type DomainHealthStatus = "healthy" | "warning" | "blocked" | "unknown";

export interface DNSBLResult {
  list: string;
  listed: boolean;
  listType: "spam" | "disposable" | "general";
}

export interface DomainHealthCheck {
  domainId: string;
  domain: string;
  status: DomainHealthStatus;
  score: number; // 0-100
  checks: {
    dnsbl: {
      checked: boolean;
      listedOn: DNSBLResult[];
      clean: DNSBLResult[];
    };
    disposable: {
      checked: boolean;
      isListed: boolean;
      detectedBy: string[];
    };
    dns: {
      mxValid: boolean;
      spfValid: boolean;
      mxRecords: string[];
      spfRecord: string | null;
    };
  };
  lastChecked: string;
  recommendations: string[];
}

export interface DomainHealthSummary {
  domains: DomainHealthCheck[];
  summary: {
    total: number;
    healthy: number;
    warning: number;
    blocked: number;
    unknown: number;
  };
  lastFullCheck: string | null;
}

// ============================================================
// Rich admin monitoring types - mirrored from the legacy public-app
// admin client. Same shape, served by the console worker now.
// ============================================================

export interface MonitoringStats {
  totalRequests: number;
  errorCount: number;
  errorRate: number;
  avgResponseTime: number;
  rpm: number;
  topEndpoints: Array<{ path: string; method: string; count: number; avgTime: number; errorCount: number }>;
  topIps: Array<{ ip: string; country: string | null; city: string | null; count: number; errorCount: number }>;
  statusBreakdown: Record<string, number>;
}

export type RequestSource = "system" | "sdk" | "agent" | "external";

export interface RequestLogEntry {
  id: string;
  requestId: string;
  method: string;
  path: string;
  statusCode: number;
  responseTimeMs: number;
  userId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  errorMessage: string | null;
  cfCountry: string | null;
  cfCity: string | null;
  source: RequestSource;
  createdAt: string;
}

export interface RequestLogResponse {
  requests: RequestLogEntry[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface TimelineBucket {
  time: string;
  requests: number;
  errors: number;
  avgResponseMs: number;
  p99ResponseMs: number;
}

export interface MonitoringTimeline {
  range: string;
  buckets: TimelineBucket[];
}

export interface KvUsageStats {
  byOperation: Array<{ operation: string; total: number }>;
  byKeyPattern: Array<{ keyPattern: string; operation: string; total: number }>;
  bySource: Array<{ source: string; total: number }>;
  timeline: Array<{ bucket: string; count: number }>;
}

export interface ErrorGroup {
  errorMessage: string;
  path: string;
  method: string;
  statusCode: number;
  count: number;
  affectedUsers: number;
  lastSeen: string;
  firstSeen: string;
}

export interface EndpointPerformance {
  path: string;
  method: string;
  count: number;
  avgMs: number;
  p95Ms: number;
  p99Ms: number;
  maxMs: number;
  errorCount: number;
  slowCount: number;
  errorRate: number;
}

export interface EmailEvent {
  id: string;
  eventType: string;
  toAddress: string;
  fromAddress: string | null;
  inboxId: string | null;
  messageId: string | null;
  domain: string | null;
  durationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface EmailPipelineStats {
  counts: Record<string, number>;
  avgProcessingMs: number;
  maxProcessingMs: number;
  recentEvents: EmailEvent[];
}

export interface UptimeCheck {
  id: string;
  status: string;
  databaseOk: boolean;
  kvOk: boolean;
  r2Ok: boolean;
  responseTimeMs: number;
  createdAt: string;
}

export interface DurableObjectStats {
  totalRoomsQueried: number;
  activeRooms: number;
  totalConnections: number;
  totalUniqueUsers: number;
  totalTrackedMessages: number;
  rooms: Array<{
    inboxId: string;
    address: string;
    teamName: string;
    connections: number;
    uniqueUsers: number;
    trackedMessages: number;
  }>;
}

export interface DbSizeStats {
  totalRows: number;
  tables: Array<{ tableName: string; rowCount: number }>;
  history: Array<{ table_name: string; row_count: number; created_at: string }>;
}

export interface AlertConfigItem {
  id: string;
  alertType: string;
  enabled: boolean;
  thresholdValue: number;
  cooldownMinutes: number;
  lastTriggeredAt: string | null;
}

export interface AlertHistoryItem {
  id: string;
  alertType: string;
  message: string;
  details: Record<string, unknown> | null;
  sentTo: string;
  createdAt: string;
}

export interface AlertsData {
  configs: AlertConfigItem[];
  history: AlertHistoryItem[];
}

export interface UptimeStats {
  uptimePercent: number;
  totalChecks: number;
  okCount: number;
  degradedCount: number;
  errorCount: number;
  avgResponseMs: number;
  serviceHealth: {
    database: number;
    kv: number;
    r2: number;
  };
  recentChecks: UptimeCheck[];
}

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

export const api = {
  auth: {
    /** Request a 6-digit code be emailed (or printed in dev). */
    login: (email: string) =>
      request<{ success: boolean; message: string; devHint?: string }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email }),
      }),
    /** Exchange (email, code) for a session JWT. Stored on success. */
    verify: async (email: string, code: string) => {
      const res = await request<{
        success: boolean;
        token: string;
        operator: { id: string; email: string; role: "admin" | "viewer" };
      }>("/auth/verify", {
        method: "POST",
        body: JSON.stringify({ email, code }),
      });
      setStoredToken(res.token);
      return res;
    },
    me: () => request<ConsoleSession>("/auth/me"),
    logout: async () => {
      try {
        await request<{ success: boolean }>("/auth/logout", { method: "POST" });
      } finally {
        setStoredToken(null);
      }
    },
  },
  overview: {
    summary: () =>
      request<{
        users: { total: number; active30d: number };
        inboxes: { active: number; createdToday: number };
        revenue: { mrr: number; sponsorsTotal: number };
      }>("/overview/summary"),
  },
  users: {
    list: (params: { q?: string; tier?: string; limit?: number; offset?: number } = {}) =>
      request<{ users: ConsoleUserRow[]; total: number; limit: number; offset: number }>(
        `/users${qs(params)}`,
      ),
    get: (id: string) => request<ConsoleUserDetail>(`/users/${id}`),
    setTier: (id: string, body: { subscriptionTier: ConsoleTier; subscriptionStatus?: string }) =>
      request<{ success: boolean }>(`/users/${id}/tier`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    /**
     * Change a member's plan. Downgrades cancel the live Stripe
     * subscription unless `stripeAction: "keep"` is passed.
     */
    setPlan: (
      id: string,
      body: {
        tier: ConsoleTier;
        stripeAction?: "cancel_now" | "cancel_at_end" | "keep";
        reason?: string;
      },
    ) =>
      request<{
        success: boolean;
        tier: string;
        status: string;
        appliedImmediately: boolean;
        cancelledSubscriptions: string[];
      }>(`/users/${id}/plan`, { method: "POST", body: JSON.stringify(body) }),
    charges: (id: string) =>
      request<{ charges: ConsoleChargeRow[]; note?: string }>(`/users/${id}/stripe/charges`),
    refund: (
      id: string,
      body: {
        chargeId: string;
        amount?: number;
        reason?: "duplicate" | "fraudulent" | "requested_by_customer";
      },
    ) =>
      request<{ success: boolean; refundId: string; amount: number; status: string }>(
        `/users/${id}/stripe/refund`,
        { method: "POST", body: JSON.stringify(body) },
      ),
    syncBilling: (id: string) =>
      request<{ synced: boolean; reason?: string; change: unknown }>(
        `/users/${id}/billing/sync`,
        { method: "POST" },
      ),
  },
  billing: {
    overview: () => request<ConsoleBillingOverview>("/billing/overview"),
    subscriptions: (
      params: { status?: string; q?: string; limit?: number; offset?: number } = {},
    ) =>
      request<{
        subscriptions: ConsoleSubscriptionHistoryRow[];
        total: number;
        limit: number;
        offset: number;
      }>(`/billing/subscriptions${qs(params)}`),
    invoices: (
      params: { status?: string; q?: string; limit?: number; offset?: number } = {},
    ) =>
      request<{
        invoices: ConsoleInvoiceHistoryRow[];
        total: number;
        limit: number;
        offset: number;
        collectedCents: number;
        failedCents: number;
      }>(`/billing/invoices${qs(params)}`),
    backfill: (body: { applyTiers?: boolean } = {}) =>
      request<ConsoleBackfillReport>("/billing/backfill", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    pruneForeign: (body: { dryRun?: boolean } = { dryRun: true }) =>
      request<ConsolePruneReport>("/billing/prune-foreign", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    reconcile: (body: { dryRun?: boolean; limit?: number } = {}) =>
      request<ConsoleReconcileReport>("/billing/reconcile", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  inboxes: {
    list: (params: { q?: string; scope?: string; limit?: number; offset?: number } = {}) =>
      request<{ inboxes: ConsoleInboxRow[]; total: number; limit: number; offset: number }>(
        `/inboxes${qs(params)}`,
      ),
    delete: (id: string) =>
      request<{ success: boolean }>(`/inboxes/${id}`, { method: "DELETE" }),
  },
  monitoring: {
    activity: () =>
      request<{
        inboxesByDay: { day: string; n: number }[];
        messagesByDay: { day: string; n: number }[];
      }>("/monitoring/activity"),
    emailHealth: () =>
      request<{ lastHour: Record<string, number>; rejectedToday: number }>(
        "/monitoring/email-health",
      ),
  },
  operators: {
    list: () => request<{ operators: ConsoleOperatorRow[] }>("/operators"),
    invite: (body: {
      email: string;
      role?: "admin" | "viewer";
      expiresAt?: string;
      notes?: string;
    }) =>
      request<{ success: boolean; id: string }>("/operators", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (
      id: string,
      body: {
        role?: "admin" | "viewer";
        revoked?: boolean;
        expiresAt?: string | null;
        notes?: string;
      },
    ) =>
      request<{ success: boolean }>(`/operators/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  },
  audit: {
    list: (limit?: number) =>
      request<{ entries: ConsoleAuditEntry[] }>(`/audit-log${qs({ limit })}`),
  },
  database: {
    tables: (scope: "product" | "console" = "product") =>
      request<{ tables: { name: string; rows: number }[] }>(
        `/database/tables?scope=${scope}`,
      ),
    rows: (
      name: string,
      params: { limit?: number; offset?: number; scope?: "product" | "console" } = {},
    ) =>
      request<{
        name: string;
        columns: { name: string; type: string; notnull: number; pk: number }[];
        rows: Record<string, unknown>[];
        total: number;
        limit: number;
        offset: number;
      }>(
        `/database/tables/${encodeURIComponent(name)}${qs({
          ...params,
          scope: params.scope ?? "product",
        })}`,
      ),
  },
  domains: {
    list: () =>
      request<{
        domains: {
          id: string;
          domain: string;
          enabled: boolean;
          priority: number;
          isDefault: boolean;
          createdAt: string;
          updatedAt: string;
        }[];
      }>("/domains"),
    create: (body: { domain: string; priority?: number; enabled?: boolean }) =>
      request<{ success: boolean; id: string }>("/domains", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (
      id: string,
      body: { enabled?: boolean; priority?: number; isDefault?: boolean },
    ) =>
      request<{ success: boolean }>(`/domains/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  },
  support: {
    list: (params: { status?: string; limit?: number; offset?: number } = {}) =>
      request<{
        tickets: {
          id: string;
          userId: string;
          userEmail: string | null;
          inboxId: string | null;
          subject: string;
          category: string;
          status: string;
          priority: string | null;
          createdAt: string;
          updatedAt: string;
        }[];
        total: number;
      }>(`/support${qs(params)}`),
    get: (id: string) =>
      request<{
        ticket: {
          id: string;
          userId: string;
          userEmail: string | null;
          inboxId: string | null;
          subject: string;
          category: string;
          status: string;
          priority: string | null;
          createdAt: string;
          updatedAt: string;
        };
        messages: {
          id: string;
          ticket_id: string;
          sender_kind: string;
          sender_id: string | null;
          body: string;
          created_at: string;
        }[];
      }>(`/support/${id}`),
    update: (id: string, body: { status?: string; priority?: string }) =>
      request<{ success: boolean }>(`/support/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    reply: (id: string, body: { body: string }) =>
      request<{ success: boolean; messageId: string }>(`/support/${id}/reply`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  adminMetrics: () =>
    request<{
      users: { total: number; active: number; byTier: Record<string, number> };
      inboxes: { total: number; current: number; active: number };
      messages: { total: number; current: number };
      domains: { total: number; verified: number };
      apiKeys: { total: number };
      recentUsers: Array<{
        id: string;
        email: string;
        tier: string;
        createdAt: string;
      }>;
    }>("/admin/metrics"),
  adminInboxes: () => request<AdminInboxAnalytics>("/admin/inboxes"),
  adminUserTimeline: (id: string) =>
    request<{
      user: {
        id: string;
        email: string;
        role: string;
        subscription_tier: string;
        subscription_status: string;
        created_at: string;
        updated_at: string;
      };
      inboxes: Array<{ id: string; address: string; created_at: string; expires_at: string; extension_count: number }>;
      tickets: Array<{ id: string; subject: string; status: string; priority: string; created_at: string }>;
      apiKeys: Array<{ id: string; name: string; last_used_at: string | null; created_at: string; revoked_at: string | null }>;
      credits: Array<{ id: string; kind: string; amount: number; reason: string | null; granted_by: string | null; expires_at: string | null; created_at: string }>;
      bans: Array<{ id: string; reason: string; banned_by: string; banned_at: string; expires_at: string | null }>;
    }>(`/admin/users/${encodeURIComponent(id)}/timeline`),
  adminStripe: {
    charges: (id: string) =>
      request<{
        charges: Array<{
          id: string;
          amount: number;
          amountRefunded: number;
          currency: string;
          status: string;
          description: string | null;
          receiptUrl: string | null;
          created: number;
          refunded: boolean;
        }>;
        note?: string;
      }>(`/admin/users/${encodeURIComponent(id)}/stripe/charges`),
    refund: (
      id: string,
      body: {
        chargeId: string;
        amount?: number;
        reason?: "duplicate" | "fraudulent" | "requested_by_customer";
      },
    ) =>
      request<{ success: boolean; refundId: string; amount: number; status: string }>(
        `/admin/users/${encodeURIComponent(id)}/stripe/refund`,
        { method: "POST", body: JSON.stringify(body) },
      ),
  },
  adminRateLimitOverrides: {
    list: () =>
      request<{
        overrides: Array<{
          id: string;
          user_id: string;
          user_email: string;
          resource: string;
          override_limit: number;
          reason: string | null;
          set_by: string | null;
          expires_at: string | null;
          created_at: string;
        }>;
      }>("/admin/rate-limit-overrides"),
    add: (body: {
      userId: string;
      resource: string;
      overrideLimit: number;
      reason?: string;
      expiresAt?: string | null;
    }) =>
      request<{ success: boolean }>("/admin/rate-limit-overrides", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    remove: (id: string) =>
      request<{ success: boolean }>(
        `/admin/rate-limit-overrides/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
  },
  adminUserWebhooks: {
    list: () =>
      request<{
        webhooks: Array<{
          id: string;
          user_id: string;
          user_email: string;
          url: string;
          events: string[];
          active: boolean;
          failure_count: number;
          last_triggered_at: string | null;
          last_failure_at: string | null;
          last_failure_reason: string | null;
          created_at: string;
        }>;
      }>("/admin/user-webhooks"),
    deliveries: (id: string, limit = 50) =>
      request<{
        deliveries: Array<{
          id: string;
          event_type: string;
          response_status: number | null;
          success: boolean;
          duration_ms: number | null;
          created_at: string;
          response_preview: string | null;
        }>;
      }>(`/admin/user-webhooks/${encodeURIComponent(id)}/deliveries?limit=${limit}`),
  },
  adminUserExport: (id: string) =>
    request<unknown>(`/admin/users/${encodeURIComponent(id)}/export`),
  adminMigrations: () =>
    request<{ applied: Array<{ filename: string; applied_at: string }> }>("/admin/migrations"),
  auditHeatmap: (days = 30) =>
    request<{
      days: number;
      buckets: Array<{ email: string; day: string; n: number }>;
      totals: Array<{ email: string; n: number }>;
    }>(`/audit-log/heatmap?days=${days}`),
  adminUserActions: {
    forceLogout: (id: string) =>
      request<{ success: boolean }>(`/admin/users/${encodeURIComponent(id)}/force-logout`, { method: "POST" }),
    ban: (id: string, body: { reason: string; expiresAt?: string | null }) =>
      request<{ success: boolean; banId: string }>(`/admin/users/${encodeURIComponent(id)}/ban`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    unban: (id: string) =>
      request<{ success: boolean }>(`/admin/users/${encodeURIComponent(id)}/unban`, { method: "POST" }),
    comp: (id: string, body: {
      kind: "comp_business" | "comp_pro" | "api_credits";
      amount: number;
      reason: string;
      expiresAt?: string | null;
    }) =>
      request<{ success: boolean }>(`/admin/users/${encodeURIComponent(id)}/comp`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  adminSearch: (q: string) =>
    request<{
      users: Array<{ id: string; email: string; subscription_tier: string }>;
      inboxes: Array<{ id: string; address: string; expires_at: string }>;
      tickets: Array<{ id: string; subject: string; status: string }>;
    }>(`/admin/search?q=${encodeURIComponent(q)}`),
  adminOutboundFailures: (range: "1h" | "24h" | "7d" = "24h") =>
    request<{
      failures: Array<{
        email: string;
        reason: string | null;
        error_code: string | null;
        context: string | null;
        created_at: string;
      }>;
      total: number;
      range: string;
    }>(`/admin/outbound-email-failures?range=${range}`),
  adminDisposableDomains: {
    list: () =>
      request<{
        domains: Array<{
          domain: string;
          added_by: string | null;
          reason: string | null;
          added_at: string;
        }>;
      }>("/admin/disposable-domains"),
    add: (body: { domain: string; reason?: string }) =>
      request<{ success: boolean }>("/admin/disposable-domains", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    remove: (domain: string) =>
      request<{ success: boolean }>(
        `/admin/disposable-domains/${encodeURIComponent(domain)}`,
        { method: "DELETE" },
      ),
  },
  adminFeatureFlags: {
    list: () =>
      request<{
        flags: Array<{
          id: string;
          name: string;
          enabled: boolean;
          description: string | null;
          rolloutUserIds: string | null;
          updatedBy: string | null;
          updatedAt: string;
        }>;
      }>("/admin/feature-flags"),
    create: (body: { name: string; enabled?: boolean; description?: string }) =>
      request<{ success: boolean }>("/admin/feature-flags", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    update: (
      name: string,
      body: { enabled?: boolean; description?: string },
    ) =>
      request<{ success: boolean }>(
        `/admin/feature-flags/${encodeURIComponent(name)}`,
        { method: "PATCH", body: JSON.stringify(body) },
      ),
  },
  adminTopConsumers: () =>
    request<{
      byInbox: Array<{ id: string; email: string; subscription_tier: string; inbox_count: number }>;
      byMessage: Array<{ id: string; email: string; subscription_tier: string; message_count: number }>;
    }>("/admin/top-consumers"),
  adminApiKeys: {
    list: () =>
      request<{
        keys: Array<{
          id: string;
          name: string;
          user_id: string;
          user_email: string;
          last_used_at: string | null;
          created_at: string;
          revoked_at: string | null;
        }>;
      }>("/admin/api-keys"),
    revoke: (id: string) =>
      request<{ success: boolean }>(`/admin/api-keys/${encodeURIComponent(id)}/revoke`, {
        method: "POST",
      }),
  },
  adminMaintenance: {
    get: () =>
      request<{
        maintenance: {
          enabled: boolean;
          message: string;
          until: string | null;
          updatedAt?: string;
          updatedBy?: string;
        };
      }>("/admin/maintenance"),
    set: (body: { enabled: boolean; message?: string; until?: string | null }) =>
      request<{ success: boolean }>("/admin/maintenance", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  },
  adminCacheStats: (range: "1h" | "6h" | "24h" | "7d" = "24h") =>
    request<{
      range: string;
      hits: number;
      misses: number;
      total: number;
      hitRatePercent: number;
    }>(`/admin/cache-stats?range=${range}`),
  adminStorageProjection: () =>
    request<{
      d1Rows: { current: number; rowsPerDay: number; daysToDouble: number | null };
      sampleCount: number;
      note?: string;
    }>("/admin/storage-projection"),
  adminAuthSpike: () =>
    request<{
      lastHour: { codes: number; verifyFailures: number };
      priorHour: { codes: number; verifyFailures: number };
      codeRatio: number | null;
      failRatio: number | null;
      spike: boolean;
    }>("/admin/auth-spike"),
  adminNotifications: {
    config: () =>
      request<{
        channels: Record<
          string,
          {
            channel: string;
            event: string;
            configured: boolean;
            secretName: string;
          }
        >;
      }>("/admin/notifications/config"),
    list: (params: {
      limit?: number;
      offset?: number;
      status?: "sent" | "failed" | "skipped";
      event?: string;
      channel?: string;
    } = {}) =>
      request<{
        notifications: Array<{
          id: string;
          channel: string;
          event: string;
          target: string | null;
          status: string;
          error: string | null;
          payload: string | null;
          durationMs: number | null;
          createdAt: string;
        }>;
        pagination: { limit: number; offset: number; total: number };
        summary: { sent: number; failed: number; skipped: number; last24h: number };
      }>(`/admin/notifications${qs(params)}`),
    test: (channel: "discord-support") =>
      request<{ success: boolean; configured: boolean; error?: string }>(
        "/admin/notifications/test",
        {
          method: "POST",
          body: JSON.stringify({ channel }),
        },
      ),
  },
  adminPulse: () =>
    request<{
      pulses: Array<{
        key: string;
        value: string | null;
        updatedAt: string;
      }>;
    }>("/admin/pulse"),
  adminMessages: {
    list: (params: {
      page?: number;
      limit?: number;
      q?: string;
      inboxId?: string;
      domain?: string;
      hasAttachments?: "1" | "0";
      sinceHours?: number;
    } = {}) =>
      request<{
        messages: Array<{
          id: string;
          inboxId: string;
          inboxAddress: string;
          domain: string;
          fromAddress: string;
          fromName: string | null;
          subject: string | null;
          bodyPreview: string | null;
          hasAttachments: boolean;
          rawSize: number;
          receivedAt: string;
        }>;
        pagination: { page: number; limit: number; total: number; totalPages: number };
        stats: {
          total: number;
          bytes: number;
          withAttachments: number;
          lastHour: number;
          last24h: number;
        };
      }>(`/admin/messages${qs(params)}`),
    bulkDelete: (body: {
      ids?: string[];
      olderThanDays?: number;
      inboxId?: string;
    }) =>
      request<{ success: boolean; deleted: number }>("/admin/messages", {
        method: "DELETE",
        body: JSON.stringify(body),
      }),
  },
  adminHealth: () =>
    request<{
      status: "healthy" | "degraded";
      checks: { database: boolean; kv: boolean; r2: boolean };
      timestamp: string;
    }>("/admin/health"),
  cleanup: {
    preview: () =>
      request<{
        inboxes: number;
        messages: number;
        attachments: number;
        agentIdentities: number;
        r2StorageBytes: number;
        sampleInboxes: Array<{
          id: string;
          address: string;
          expires_at: string;
          created_at: string;
        }>;
      }>("/admin/cleanup/expired-inboxes/preview"),
    run: (gracePeriodMinutes: number) =>
      request<{
        expiredInboxesDeleted: number;
        messagesDeleted: number;
        r2ObjectsDeleted: number;
        durationMs: number;
      }>("/admin/cleanup/expired-inboxes", {
        method: "POST",
        body: JSON.stringify({ gracePeriodMinutes }),
      }),
  },
  crons: {
    health: () =>
      request<{
        crons: Array<{
          cronName: string;
          account: string | null;
          lastStatus: "started" | "success" | "failure";
          lastRunAt: string;
          ageSeconds: number;
          durationMs: number | null;
          message: string | null;
          expectedIntervalSeconds: number | null;
          consecutiveFailures: number;
          stale: boolean;
        }>;
        summary: { total: number; healthy: number; stale: number; failing: number };
        fetchedAt: string;
      }>("/cron/health"),
  },
  events: {
    list: (params: {
      limit?: number;
      offset?: number;
      severity?: "info" | "warning" | "error" | "critical";
      kind?: string;
      source?: string;
      status?: "open" | "resolved" | "all";
    } = {}) =>
      request<{
        events: Array<{
          id: string;
          source: string;
          kind: string;
          severity: "info" | "warning" | "error" | "critical";
          title: string;
          message: string | null;
          targetKind: string | null;
          targetId: string | null;
          payload: string | null;
          resolvedAt: string | null;
          resolvedBy: string | null;
          createdAt: string;
        }>;
        pagination: { limit: number; offset: number; total: number };
        breakdown: {
          byKind: Array<{ kind: string; count: number }>;
          bySeverity: Record<string, number>;
          open: number;
        };
      }>(`/events${qs(params)}`),
    resolve: (id: string) =>
      request<{ success: boolean }>(`/events/${id}/resolve`, { method: "POST" }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/events/${id}`, { method: "DELETE" }),
    bulkDelete: (body: {
      ids?: string[];
      olderThanDays?: number;
      status?: "resolved";
    }) =>
      request<{ success: boolean; deleted: number }>("/events", {
        method: "DELETE",
        body: JSON.stringify(body),
      }),
  },
  domainHealth: {
    list: () => request<DomainHealthSummary>("/admin/domains/health"),
    check: (id: string) =>
      request<DomainHealthCheck>(`/admin/domains/${id}/health`, { method: "POST" }),
    checkAll: () =>
      request<DomainHealthSummary>("/admin/domains/health/check-all", { method: "POST" }),
    history: (id: string, limit?: number) =>
      request<{ history: DomainHealthCheck[] }>(
        `/admin/domains/${id}/health/history${limit ? `?limit=${limit}` : ""}`,
      ),
  },
  /**
   * Rich admin monitoring (request log, KV usage, uptime, DOs, etc.).
   * Backed by the existing admin Hono router mounted under /admin on
   * the console worker.
   */
  adminMonitoring: {
    stats: (range: string, source = "all") =>
      request<MonitoringStats>(`/admin/monitoring/stats?range=${range}&source=${source}`),
    timeline: (range: string, source = "all") =>
      request<MonitoringTimeline>(`/admin/monitoring/timeline?range=${range}&source=${source}`),
    requests: (params: {
      range?: string;
      source?: string;
      page?: number;
      limit?: number;
      method?: string;
      status?: string;
      ip?: string;
      path?: string;
    } = {}) =>
      request<RequestLogResponse>(`/admin/monitoring/requests${qs(params)}`),
    requestDetail: (id: string) =>
      request<RequestLogEntry>(`/admin/monitoring/requests/${id}`),
    kv: (range: string) =>
      request<KvUsageStats>(`/admin/monitoring/kv?range=${range}`),
    errors: (range: string, source = "all") =>
      request<{ errors: ErrorGroup[] }>(
        `/admin/monitoring/errors?range=${range}&source=${source}`,
      ),
    endpoints: (range: string, source = "all") =>
      request<{ endpoints: EndpointPerformance[] }>(
        `/admin/monitoring/endpoints?range=${range}&source=${source}`,
      ),
    email: (range: string) =>
      request<EmailPipelineStats>(`/admin/monitoring/email?range=${range}`),
    uptime: (range: string) =>
      request<UptimeStats>(`/admin/monitoring/uptime?range=${range}`),
    durableObjects: () =>
      request<DurableObjectStats>(`/admin/monitoring/durable-objects`),
    dbSize: (range: string) =>
      request<DbSizeStats>(`/admin/monitoring/db-size?range=${range}`),
    alerts: () => request<AlertsData>(`/admin/monitoring/alerts`),
    testAlert: () =>
      request<{ success: boolean }>(`/admin/monitoring/alerts/test`, {
        method: "POST",
      }),
    updateAlert: (
      alertType: string,
      body: { enabled?: boolean; thresholdValue?: number; cooldownMinutes?: number },
    ) =>
      request<{ success: boolean }>(`/admin/monitoring/alerts/${alertType}`, {
        method: "PUT",
        body: JSON.stringify(body),
      }),
    purge: (olderThanDays: number) =>
      request<{ deleted: number }>(
        `/admin/monitoring/requests?olderThanDays=${olderThanDays}`,
        { method: "DELETE" },
      ),
  },
  platform: {
    usage: (scope: "product" | "console" | "services" = "product") =>
      request<
        | {
            available: false;
            scope: "product" | "console" | "services";
            reason: string;
            message: string;
            limits: {
              id: string;
              service: string;
              label: string;
              free: number | null;
              paid: number | null;
              period: "day" | "month" | "total";
              unit: string;
              description: string;
            }[];
          }
        | {
            available: true;
            scope: "product" | "console" | "services";
            fetchedAt: string;
            metrics: {
              id: string;
              label: string;
              service: string;
              current: number;
              free: number | null;
              paid: number | null;
              percentFree: number;
              projected: number;
              period: "day" | "month" | "total";
              daysLeft: number;
              unit: string;
              description: string;
              status: "ok" | "warn" | "alert" | "over";
            }[];
            errors: { section: string; message: string }[];
            plan: { usageModel: string | null; greenCompute: boolean | null } | null;
            deploys: Array<{ name: string; lastModified: string; createdOn: string }>;
            projectedCostUsd: number;
          }
      >(`/platform/usage?scope=${scope}`),
  },
  sponsors: {
    list: (params: { limit?: number; offset?: number } = {}) =>
      request<{
        sponsors: {
          id: string;
          stripePaymentId: string;
          name: string | null;
          email: string | null;
          isAnonymous: boolean;
          amount: number;
          currency: string;
          showAmount: boolean;
          message: string | null;
          donatedAt: string;
        }[];
        total: number;
        totalAmountCents: number;
      }>(`/sponsors${qs(params)}`),
    update: (id: string, body: { showAmount?: boolean; isAnonymous?: boolean }) =>
      request<{ success: boolean }>(`/sponsors/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
  },
};
